import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ShieldCheck, Calendar, Wallet, ShoppingCart, Receipt, Users, Boxes,
  Route, Gauge, MapPin, UserCheck, Truck, Package, Trophy, CreditCard,
  HelpCircle, Loader2, TrendingUp, CheckCircle2, XCircle, Clock, Target,
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const PALETA = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#a855f7', '#0ea5e9']
const FORMA_LABEL: any = {
  dinheiro: 'Dinheiro', boleto: 'Boleto', prazo: 'A prazo', cheque: 'Cheque',
  pix: 'PIX', cartao_credito: 'Cartão', cartao: 'Cartão', troca: 'Troca', bonificacao: 'Bonificação', '—': 'Não informado',
}
const fmtKm = (v: number | null | undefined) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`)

// Tooltip (?) explicando de onde vem cada dado
function Ajuda({ children }: { children: React.ReactNode }) {
  return (
    <span className="group relative inline-flex">
      <HelpCircle size={14} className="text-ink-400 cursor-help" />
      <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 top-5 z-30 w-64
        bg-ink-800 text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg font-normal">
        {children}
      </span>
    </span>
  )
}

function Secao({ titulo, icon, ajuda, children, className = '' }: any) {
  return (
    <div className={`bg-white rounded-2xl border border-ink-200 shadow-sm p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-semibold text-ink-800 text-sm">{titulo}</h3>
        {ajuda && <Ajuda>{ajuda}</Ajuda>}
      </div>
      {children}
    </div>
  )
}

function CardKPI({ titulo, valor, sub, cor, icon, texto, ajuda }: any) {
  const cores: any = {
    violet: 'from-violet-500 to-violet-600', blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600', amber: 'from-amber-500 to-amber-600',
    cyan: 'from-cyan-500 to-cyan-600', rose: 'from-rose-500 to-rose-600', slate: 'from-slate-400 to-slate-500',
  }
  return (
    <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cores[cor]} text-white grid place-items-center`}>{icon}</div>
        <span className="text-sm font-medium text-ink-600">{titulo}</span>
        {ajuda && <Ajuda>{ajuda}</Ajuda>}
      </div>
      <div className="mt-2 text-2xl font-bold text-ink-800">{texto ? valor : formatMoney(valor)}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  )
}

function RankingBarras({ itens, cor, mostrarQtd }: any) {
  if (!itens || itens.length === 0) return <Vazio />
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, itens.length * 30)}>
      <BarChart data={itens} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
        <YAxis type="category" dataKey="nome" width={150} fontSize={10}
          tickFormatter={(v) => (v.length > 24 ? v.slice(0, 24) + '…' : v)} />
        <Tooltip formatter={(v: any, _n: any, p: any) => [formatMoney(v), mostrarQtd ? `Qtd: ${p.payload.qtd}` : 'Total']} />
        <Bar dataKey="total" radius={[0, 4, 4, 0]} fill={cor} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function Vazio({ txt = 'Sem dados no dia' }: { txt?: string }) {
  return <div className="text-center text-ink-400 text-sm py-8">{txt}</div>
}
function CarregandoBox() {
  return <div className="grid place-items-center py-20 text-ink-400"><Loader2 className="animate-spin" size={28} /></div>
}

// Barrinha de % (cumprimento / positivação)
function Pct({ v, cor }: { v: number | null; cor: string }) {
  if (v == null) return <span className="text-ink-300">—</span>
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, v)}%`, background: cor }} />
      </div>
      <span className="text-xs font-bold text-ink-700 w-9 text-right">{v}%</span>
    </div>
  )
}

const hojeISO = () => { const d = new Date(); d.setHours(d.getHours() - 3); return d.toISOString().slice(0, 10) }

const PERIODOS = [
  ['dia', 'Dia'], ['ontem', 'Ontem'], ['7d', 'Últimos 7 dias'],
  ['30d', 'Últimos 30 dias'], ['mes', 'Este mês'], ['custom', 'Personalizado'],
] as const

// Calcula o intervalo (de, ate) de cada preset, no fuso de Fortaleza (UTC-3).
function rangeFor(p: string): [string, string] {
  const base = new Date(); base.setHours(base.getHours() - 3)
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  const hoje = iso(base)
  if (p === 'ontem') { const y = new Date(base); y.setDate(y.getDate() - 1); return [iso(y), iso(y)] }
  if (p === '7d') { const s = new Date(base); s.setDate(s.getDate() - 6); return [iso(s), hoje] }
  if (p === '30d') { const s = new Date(base); s.setDate(s.getDate() - 29); return [iso(s), hoje] }
  if (p === 'mes') { const s = new Date(base.getFullYear(), base.getMonth(), 1); return [iso(s), hoje] }
  return [hoje, hoje]
}

// ============================================================================
export default function PainelAuditoriaPage() {
  const [periodo, setPeriodo] = useState<string>('dia')
  const [dia, setDia] = useState(hojeISO())
  const [de, setDe] = useState(hojeISO())
  const [ate, setAte] = useState(hojeISO())

  const params: any =
    periodo === 'dia' ? { data: dia }
    : periodo === 'custom' ? (de && ate ? { de, ate } : { data: hojeISO() })
    : (() => { const [d1, d2] = rangeFor(periodo); return { de: d1, ate: d2 } })()

  const { data: d, isLoading } = useQuery<any>({
    queryKey: ['painel-auditoria', params],
    queryFn: async () => (await api.get('/comercial/painel-auditoria', { params })).data,
  })

  return (
    <div className="p-4 lg:p-6 space-y-4 bg-ink-50/40 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-ink-800">
            <ShieldCheck className="text-violet-600" size={26} /> Painel de Auditoria
          </h1>
          <p className="text-xs text-ink-500 mt-0.5">
            Conferência por dia ou período — rota, km, vendas, formas de pagamento e clientes.
            Passe o mouse no <HelpCircle size={12} className="inline text-ink-400" /> de cada bloco pra ver de onde vem o dado.
            {d?.label && <> Mostrando: <b className="text-ink-600">{d.label}</b>.</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={16} className="text-ink-400" />
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
            className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white">
            {PERIODOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {periodo === 'dia' && (
            <input type="date" value={dia} max={hojeISO()} onChange={(e) => setDia(e.target.value)}
              className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white" />
          )}
          {periodo === 'custom' && (
            <>
              <input type="date" value={de} max={hojeISO()} onChange={(e) => setDe(e.target.value)}
                className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
              <span className="text-ink-400 text-sm">até</span>
              <input type="date" value={ate} max={hojeISO()} onChange={(e) => setAte(e.target.value)}
                className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
            </>
          )}
        </div>
      </div>

      {isLoading || !d ? <CarregandoBox /> : <Conteudo d={d} />}
    </div>
  )
}

function Conteudo({ d }: { d: any }) {
  const k = d.kpis
  const cob = d.cobertura || { rows: [], totais: {} }
  const ct = cob.totais || {}
  const fmtDur = (s?: number) => {
    if (!s) return '—'
    const m = Math.round(s / 60)
    return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m} min`
  }

  return (
    <div className="space-y-4">
      {/* ===== KPIs do dia ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <CardKPI titulo="Faturamento" valor={k.faturamento} cor="violet" icon={<Wallet size={18} />}
          sub={`${k.pedidos} pedidos`}
          ajuda="Soma do total dos pedidos do dia confirmados (status confirmado→entregue), no fuso de Fortaleza. Exclui bonificação e troca." />
        <CardKPI titulo="Pedidos" valor={String(k.pedidos)} texto cor="blue" icon={<ShoppingCart size={18} />}
          sub={`ticket ${formatMoney(k.ticket_medio)}`}
          ajuda="Quantidade de pedidos de venda do dia. Ticket = faturamento ÷ pedidos." />
        <CardKPI titulo="Itens vendidos" valor={k.itens.toLocaleString('pt-BR')} texto cor="amber" icon={<Boxes size={18} />}
          sub="soma das quantidades"
          ajuda="Soma das quantidades de todos os itens dos pedidos do dia (tabela pedido_itens)." />
        <CardKPI titulo="Clientes atendidos" valor={String(k.clientes)} texto cor="emerald" icon={<Users size={18} />}
          sub="compraram no período"
          ajuda="Clientes distintos que tiveram pedido de venda no dia (COUNT DISTINCT cliente)." />
        <CardKPI titulo="Km rodado (vendedores)" valor={fmtKm(ct.km)} texto cor="cyan" icon={<Gauge size={18} />}
          sub={`${ct.vendedores_ativos ?? 0} em campo`}
          ajuda="Km percorrido (GPS do celular) somando os vendedores que rodaram no período. Vem do app do vendedor." />
      </div>

      {/* ===== Cumprimento de rota — VENDEDOR ===== */}
      <Secao
        titulo="Cumprimento de rota — vendedor"
        icon={<Route size={16} className="text-violet-600" />}
        ajuda="Vem do APP DO VENDEDOR. Programados do dia = carteira do vendedor ÷ ciclo de visita (15 ou 21 dias) — ele cobre a carteira inteira a cada ciclo, visitando ~carteira/ciclo por dia. Visitados = check-ins. Positivados = visitas que geraram pedido. Tempo = média entre check-in e check-out. Km = trajeto GPS.">
        {cob.erro ? (
          <div className="text-center text-amber-600 text-sm py-6">App do vendedor indisponível agora ({cob.erro}). Tente de novo em instantes.</div>
        ) : (cob.rows || []).length === 0 ? (
          <Vazio txt="Nenhuma rota/visita de vendedor neste período." />
        ) : (
          <>
            {/* resumo */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <MiniRota label="Em campo" valor={ct.vendedores_ativos ?? 0} icon={<UserCheck size={15} />} cor="violet"
                sub="vendedores c/ visita" />
              <MiniRota label="Programados" valor={ct.programados ?? 0} icon={<Target size={15} />}
                sub="clientes do dia" />
              <MiniRota label="Visitados" valor={ct.visitados ?? 0} icon={<CheckCircle2 size={15} />} cor="emerald"
                sub={ct.cumprimento_pct != null ? `cumprimento ${ct.cumprimento_pct}%` : 'cumprimento —'} />
              <MiniRota label="Positivação" valor={ct.positivacao_pct != null ? `${ct.positivacao_pct}%` : '—'}
                icon={<TrendingUp size={15} />} cor="emerald" sub={`${ct.positivados ?? 0} c/ pedido`} />
              <MiniRota label="Km rodado" valor={fmtKm(ct.km)} icon={<Gauge size={15} />} cor="cyan" sub="trajeto GPS" />
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-ink-500 text-xs border-b">
                    <th className="text-left py-1.5">Vendedor</th>
                    <th className="text-center">Programados</th>
                    <th className="text-center">Visitados</th>
                    <th className="text-left w-32">Cumprimento</th>
                    <th className="text-center">Positivados</th>
                    <th className="text-center">Positivação</th>
                    <th className="text-right">Tempo médio</th>
                    <th className="text-right">Km</th>
                  </tr>
                </thead>
                <tbody>
                  {cob.rows.map((v: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                      <td className="py-2 font-medium text-ink-800">
                        {v.vendedor}
                        {v.ciclo
                          ? <span className="ml-1.5 text-[10px] font-semibold text-violet-600 bg-violet-50 rounded px-1 py-0.5">ciclo {v.ciclo}d</span>
                          : <span className="ml-1.5 text-[10px] text-amber-500">sem ciclo</span>}
                      </td>
                      <td className="text-center text-ink-600">{v.programados}
                        {v.prog_diario ? <span className="block text-[10px] text-ink-400">{v.prog_diario}/dia</span> : null}</td>
                      <td className="text-center font-semibold text-ink-800">{v.visitados}</td>
                      <td><Pct v={v.cumprimento_pct} cor="#8b5cf6" /></td>
                      <td className="text-center font-semibold text-emerald-600">{v.positivados}</td>
                      <td className="text-center">{v.positivacao_pct != null ? `${v.positivacao_pct}%` : '—'}</td>
                      <td className="text-right text-ink-500 whitespace-nowrap">
                        {v.tempo_medio_seg ? <span className="flex items-center justify-end gap-1"><Clock size={12} />{fmtDur(v.tempo_medio_seg)}</span> : '—'}
                      </td>
                      <td className="text-right text-cyan-700 font-medium">{fmtKm(v.km)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-ink-400 mt-2">
              <b>Programados do dia = carteira ÷ ciclo</b> (15 ou 21 dias por vendedor): a meta diária de visitas
              pra cobrir a carteira inteira dentro do ciclo. No período, multiplica pela quantidade de dias.
              Visitas, positivação e km dependem do vendedor usar o app em campo.
            </p>
          </>
        )}
      </Secao>

      {/* ===== Grid de vendas ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Formas de pagamento */}
        <Secao titulo="Vendas por forma de pagamento" icon={<CreditCard size={16} className="text-emerald-600" />}
          ajuda="Pedidos de venda do dia agrupados pela forma de pagamento do pedido. Mesma base do faturamento (exclui bonificação e troca).">
          {d.formas_pagamento.length === 0 ? <Vazio /> : (
            <div className="flex items-center gap-3">
              <ResponsiveContainer width="50%" height={190}>
                <PieChart>
                  <Pie data={d.formas_pagamento} dataKey="valor" nameKey="forma" innerRadius={45} outerRadius={78} paddingAngle={2}>
                    {d.formas_pagamento.map((_: any, i: number) => <Cell key={i} fill={PALETA[i % PALETA.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1 text-sm">
                {d.formas_pagamento.map((f: any, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PALETA[i % PALETA.length] }} />
                    <span className="flex-1">{FORMA_LABEL[f.forma] || f.forma} <span className="text-ink-400 text-xs">({f.qtd})</span></span>
                    <span className="font-semibold">{formatMoney(f.valor)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Secao>

        {/* Faturamento por tipo */}
        <Secao titulo="Faturamento por tipo de operação" icon={<Receipt size={16} className="text-blue-600" />}
          ajuda="TODAS as operações do dia, inclusive bonificação e troca — para conferência fiscal. 'Venda fiscal' tem NF-e; 'simples' é venda sem nota.">
          {d.faturamento_tipo.length === 0 ? <Vazio /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-500 text-xs border-b">
                <th className="text-left py-1.5">Tipo</th><th className="text-right">Qtd</th><th className="text-right">Valor</th>
              </tr></thead>
              <tbody>
                {d.faturamento_tipo.map((t: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                    <td className="py-2 font-medium">{t.tipo}</td>
                    <td className="text-right text-ink-500">{t.qtd}</td>
                    <td className="text-right font-semibold">{formatMoney(t.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Secao>

        {/* Produtos mais vendidos */}
        <Secao titulo="Produtos mais vendidos" icon={<Package size={16} className="text-violet-600" />}
          ajuda="Top 15 produtos por valor vendido no dia (tabela pedido_itens). A quantidade aparece no tooltip da barra.">
          <RankingBarras itens={d.produtos} cor="#8b5cf6" mostrarQtd />
        </Secao>

        {/* Principais clientes */}
        <Secao titulo="Principais clientes" icon={<Trophy size={16} className="text-amber-500" />}
          ajuda="Top 10 clientes por valor comprado no dia. O número entre parênteses no tooltip é a quantidade de pedidos.">
          <RankingBarras itens={d.clientes} cor="#3b82f6" mostrarQtd />
        </Secao>
      </div>

      {/* Vendas por vendedor */}
      <Secao titulo="Vendas por vendedor" icon={<TrendingUp size={16} className="text-blue-600" />}
        ajuda="Total vendido por cada vendedor no dia. Vincula pelo vendedor_id do pedido.">
        {d.vendedores.length === 0 ? <Vazio /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-ink-500 text-xs border-b">
              <th className="text-left py-1.5">Vendedor</th><th className="text-right">Pedidos</th><th className="text-right">Venda</th>
            </tr></thead>
            <tbody>
              {d.vendedores.map((v: any, i: number) => (
                <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                  <td className="py-2 font-medium">{v.nome}</td>
                  <td className="text-right text-ink-500">{v.qtd}</td>
                  <td className="text-right font-semibold">{formatMoney(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Secao>
    </div>
  )
}

function MiniRota({ label, valor, sub, icon, cor = 'slate' }: any) {
  const cores: any = { slate: 'text-ink-700', emerald: 'text-emerald-600', violet: 'text-violet-600', cyan: 'text-cyan-600' }
  return (
    <div className="bg-ink-50 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-ink-500 uppercase tracking-wide">{icon}{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${cores[cor]}`}>{valor}</div>
      {sub && <div className="text-[11px] text-ink-400">{sub}</div>}
    </div>
  )
}

function StatusChip({ s }: { s: string }) {
  const map: any = {
    faturada: ['Faturada', 'bg-blue-100 text-blue-700'],
    em_rota: ['Em rota', 'bg-amber-100 text-amber-700'],
    em_acerto: ['Em acerto', 'bg-violet-100 text-violet-700'],
    fechada: ['Fechada', 'bg-emerald-100 text-emerald-700'],
    cancelada: ['Cancelada', 'bg-rose-100 text-rose-700'],
  }
  const [txt, cls] = map[s] || [s, 'bg-ink-100 text-ink-600']
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{txt}</span>
}
